import { useCallback, useEffect, useRef, useState } from 'react';
import { streamChatWithCoach } from '@shared/services/geminiService';
import { logWarn } from '@/lib/log';
import {
  CHAT_RESUME_WINDOW_DAYS,
  getChatSession,
  getChatSessionMessages,
  getLatestChatSession,
  listRecentChatSessions,
} from '@shared/services/chatSessionService';
import { ChatMessage, ChatSession } from '@shared/types';
import { mergePrivacyLexicons } from '@shared/utils/chatPrivacyTokens';

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'model',
  text: "Hi, I'm your financial assistant. Ask me about your spending, debt, subscriptions, and accounts — I'll use your connected data to keep answers specific to you.",
  timestamp: new Date(),
};

/** Free tier: reset the conversation after this much inactivity. */
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;

/** Members: silently pick the previous conversation back up within this window. */
const AUTO_RESUME_WINDOW_MS = 15 * 60 * 1000;

interface UseStreamingChatOptions {
  uid: string | undefined;
  /**
   * Member (data_sync) session persistence: auto-resume the latest recent
   * conversation, keep sessions across inactivity, and expose the
   * last-30-days resume list. undefined = entitlement still loading.
   */
  canResume: boolean | undefined;
}

export function useStreamingChat({ uid, canResume }: UseStreamingChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [remainingMessages, setRemainingMessages] = useState<number | undefined>();
  const [privacyLexicon, setPrivacyLexicon] = useState<Record<string, string>>({});
  const [recentSessions, setRecentSessions] = useState<ChatSession[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | undefined>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoResumeAttemptedRef = useRef(false);

  const startNewChat = useCallback(() => {
    sessionIdRef.current = undefined;
    setMessages([{ ...WELCOME_MESSAGE, timestamp: new Date() }]);
    setIsStreaming(false);
    setPrivacyLexicon({});
  }, []);

  // Free-tier inactivity reset. Members keep their conversation — their
  // sessions are resumable anyway, so wiping state would only lose context.
  useEffect(() => {
    if (canResume !== false) return;
    if (messages.length <= 1) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(startNewChat, SESSION_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [messages, canResume, startNewChat]);

  /** Load a stored session (messages + privacy lexicon) into the chat. */
  const resumeSession = useCallback(async (session: ChatSession): Promise<boolean> => {
    if (!uid) return false;
    setIsLoadingHistory(true);
    try {
      // Re-read the session doc alongside the messages: the lexicon on the
      // cached resume-list entry can be stale, and rendering history with a
      // stale map degrades mapped account names to generic nouns.
      const [history, fresh] = await Promise.all([
        getChatSessionMessages(uid, session.id),
        getChatSession(uid, session.id).catch(() => null),
      ]);
      if (history.length === 0) return false;
      sessionIdRef.current = session.id;
      setPrivacyLexicon((prev) =>
        mergePrivacyLexicons(
          mergePrivacyLexicons(prev, session.privacyLexicon),
          fresh?.privacyLexicon,
        ),
      );
      setMessages([WELCOME_MESSAGE, ...history]);
      return true;
    } catch (err) {
      logWarn('Failed to resume chat session:', err);
      return false;
    } finally {
      setIsLoadingHistory(false);
    }
  }, [uid]);

  /** Refresh the member resume list (sessions from the last 30 days). */
  const loadRecentSessions = useCallback(async (): Promise<ChatSession[]> => {
    if (!uid || !canResume) return [];
    try {
      const sessions = await listRecentChatSessions(uid, CHAT_RESUME_WINDOW_DAYS);
      setRecentSessions(sessions);
      return sessions;
    } catch (err) {
      logWarn('Failed to list recent chat sessions:', err);
      return [];
    }
  }, [uid, canResume]);

  // Members: on first mount, quietly pick up a conversation the user was in
  // the middle of (e.g. they tapped a deep link and the app reloaded).
  useEffect(() => {
    if (!uid || canResume !== true || autoResumeAttemptedRef.current) return;
    autoResumeAttemptedRef.current = true;

    (async () => {
      try {
        const session = await getLatestChatSession(uid);
        if (!session || session.messageCount === 0) return;
        const elapsed = Date.now() - new Date(session.lastMessageAt).getTime();
        if (elapsed > AUTO_RESUME_WINDOW_MS) return;
        await resumeSession(session);
      } catch (err) {
        logWarn('Chat auto-resume failed:', err);
      }
    })();
  }, [uid, canResume, resumeSession]);

  // Resolves true when a reply was delivered (or the user aborted mid-stream),
  // false when the send failed outright — callers use this to refund the
  // free-tier message count.
  const sendMessage = useCallback(async (text: string): Promise<boolean> => {
    const trimmed = text.trim();
    if (!trimmed) return true;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: trimmed,
      timestamp: new Date(),
    };

    const botMsgId = (Date.now() + 1).toString();

    // Build history from current messages (before adding the new user
    // message), excluding the client-only welcome bubble.
    const history = messages
      .filter(m => m.id !== 'welcome')
      .map(m => ({
        role: m.role === 'model' ? 'model' : 'user',
        text: m.text,
      }));

    setMessages(prev => [...prev, userMsg]);
    setIsStreaming(true);

    // Empty bot message placeholder for streaming tokens
    const botPlaceholder: ChatMessage = {
      id: botMsgId,
      role: 'model',
      text: '',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, botPlaceholder]);

    const controller = new AbortController();
    abortRef.current = controller;

    let streamErrored = false;
    try {
      await streamChatWithCoach(history, trimmed, {
        onToken: (token) => {
          setMessages(prev =>
            prev.map(m =>
              m.id === botMsgId ? { ...m, text: m.text + token } : m
            )
          );
        },
        onMetadata: (data) => {
          if (data.remainingMessages !== undefined) {
            setRemainingMessages(data.remainingMessages);
          }
          if (data.sessionId) {
            sessionIdRef.current = data.sessionId;
          }
          if (data.privacyLexicon && Object.keys(data.privacyLexicon).length > 0) {
            setPrivacyLexicon((prev) => mergePrivacyLexicons(prev, data.privacyLexicon));
          }
        },
        onDone: () => {
          setIsStreaming(false);
        },
        onError: (error) => {
          streamErrored = true;
          setMessages(prev =>
            prev.map(m =>
              m.id === botMsgId
                ? { ...m, text: error || 'Something went wrong. Please try again.' }
                : m
            )
          );
          setIsStreaming(false);
        },
      }, controller.signal, sessionIdRef.current);
      return !streamErrored;
    } catch (err) {
      if (controller.signal.aborted) {
        setIsStreaming(false);
        return true;
      }
      logWarn('Chat stream failed:', err);
      setMessages(prev =>
        prev.map(m =>
          m.id === botMsgId
            ? { ...m, text: "I couldn't connect just now. Please check your connection and try again." }
            : m
        )
      );
      setIsStreaming(false);
      return false;
    }
  }, [messages]);

  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  return {
    messages,
    isStreaming,
    isLoadingHistory,
    remainingMessages,
    sendMessage,
    cancelStream,
    privacyLexicon,
    recentSessions,
    loadRecentSessions,
    resumeSession,
    startNewChat,
    activeSessionId: sessionIdRef.current,
  };
}
